import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Clock, CheckCircle2, Lock, Scan, Eye, Zap, Calendar, Target, Sparkles, Binary } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  })
};
interface QuizIntroProps {
  onStart: () => void;
}
export function QuizIntro({
  onStart
}: QuizIntroProps) {
  return <div className="min-h-screen bg-matrix-bg" data-id="element-279">
      {/* Hero */}
      <section className="relative overflow-hidden px-5 pt-16 pb-12 md:pt-24 md:pb-20" data-id="element-280">
        {/* Subtle radial glow */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_50%_-10%,rgba(201,168,76,0.08),transparent_60%)]" data-id="element-281" />

        <div className="relative mx-auto max-w-2xl text-center" data-id="element-282">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0} className="mb-4 inline-flex items-center gap-2 rounded-full border border-matrix-gold/20 bg-matrix-gold/5 px-4 py-1.5" data-id="element-283">
            <Binary className="h-3.5 w-3.5 text-matrix-gold" data-id="element-284" />
            <span className="font-body text-xs font-medium tracking-wider text-matrix-gold uppercase" data-id="element-285">
              Test gratuito
            </span>
          </motion.div>

          <motion.h1 variants={fadeUp} initial="hidden" animate="visible" custom={1} className="font-heading text-3xl font-bold leading-tight text-matrix-text md:text-5xl md:leading-tight" data-id="element-286">
            Descubre tu{' '}
            <span className="text-matrix-gold" data-id="element-287">"Tipo de Jugador"</span> en la
            Matrix
          </motion.h1>

          <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={2} className="mx-auto mt-5 max-w-xl font-body text-base leading-relaxed text-matrix-text-muted md:text-lg" data-id="element-288">
            Responde 14 preguntas rápidas y recibe tu{' '}
            <span className="font-medium text-matrix-text" data-id="element-289">
              Mapa del Jugador&#8482;
            </span>
            : el patrón oculto que te está repitiendo la misma historia… y el
            primer paso para salir del loop sin perderte a ti mismo.
          </motion.p>

          {/* Benefits */}
          <motion.ul variants={fadeUp} initial="hidden" animate="visible" custom={3} className="mx-auto mt-8 max-w-lg space-y-3 text-left" data-id="element-290">
            {[{
            icon: Scan,
            text: 'Identifica qué "programa" te está drenando: miedo, control, confusión o auto-mejora infinita'
          }, {
            icon: Eye,
            text: 'Descubre por qué la manifestación / sanación / meditación a veces no te pega'
          }, {
            icon: Calendar,
            text: 'Recibe un mini-plan personalizado para sentir claridad en los próximos 7 días'
          }].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-291">
                <item.icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-matrix-gold" data-id="element-292" />
                <span className="font-body text-sm leading-snug text-matrix-text-muted md:text-base" data-id="element-293">
                  {item.text}
                </span>
              </li>)}
          </motion.ul>

          {/* CTA */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={4} className="mt-10" data-id="element-294">
            <button onClick={onStart} className="group inline-flex items-center gap-2.5 rounded-lg bg-gradient-to-r from-matrix-gold to-matrix-gold-bright px-8 py-4 font-body text-sm font-semibold uppercase tracking-wider text-matrix-bg shadow-[0_0_32px_rgba(201,168,76,0.2)] transition-all duration-300 hover:shadow-[0_0_48px_rgba(201,168,76,0.35)] hover:scale-[1.02] active:scale-[0.98]" data-id="element-295">
              Empezar test gratis
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" data-id="element-296" />
            </button>
          </motion.div>

          {/* Trust line */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={5} className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2" data-id="element-297">
            {[{
            icon: Clock,
            label: 'Toma 2–3 minutos'
          }, {
            icon: CheckCircle2,
            label: 'No necesitas experiencia espiritual'
          }, {
            icon: Lock,
            label: 'Tus respuestas son privadas'
          }].map((t, i) => <span key={i} className="inline-flex items-center gap-1.5 font-body text-xs text-matrix-text-dim" data-id="element-298">
                <t.icon className="h-3.5 w-3.5 text-matrix-gold-muted" data-id="element-299" />
                {t.label}
              </span>)}
          </motion.div>
        </div>
      </section>

      {/* Social proof / identity */}
      <section className="border-t border-matrix-border/40 px-5 py-14 md:py-20" data-id="element-300">
        <div className="mx-auto max-w-2xl" data-id="element-301">
          <motion.h2 initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.6
        }} className="font-heading text-2xl font-semibold text-matrix-text md:text-3xl" data-id="element-302">
            Hay 2 tipos de humanos…
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="mt-4 font-body text-base leading-relaxed text-matrix-text-muted md:text-lg" data-id="element-303">
            Los que pasan la vida reaccionando al tablero.
            <br data-id="element-304" />Y los que se dan cuenta de que{' '}
            <span className="font-medium text-matrix-gold" data-id="element-305">
              ellos sostienen el control
            </span>
            .
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="mt-4 font-body text-sm leading-relaxed text-matrix-text-muted" data-id="element-306">
            Este test no es "otro quiz random". Fue diseñado para personas que
            sienten:
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="mt-6 space-y-3" data-id="element-307">
            {['"Sé que la realidad es más profunda… pero no veo el código."', '"Estoy cansado/a de arreglarme sin parar."', '"Quiero despertar, pero sin volverme extremo/a ni perder mi centro."'].map((quote, i) => <div key={i} className="rounded-lg border border-matrix-border/50 bg-matrix-card/60 px-5 py-3.5" data-id="element-308">
                <p className="font-heading text-sm italic text-matrix-text/90 md:text-base" data-id="element-309">
                  {quote}
                </p>
              </div>)}
          </motion.div>

          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="mt-6 font-body text-xs text-matrix-text-dim" data-id="element-310">
            Usado por buscadores de consciencia en +25 países (lectores,
            meditadores y curiosos de la Matrix).
          </motion.p>
        </div>
      </section>

      {/* What you'll get */}
      <section className="border-t border-matrix-border/40 px-5 py-14 md:py-20" data-id="element-311">
        <div className="mx-auto max-w-2xl" data-id="element-312">
          <motion.h2 initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.6
        }} className="font-heading text-2xl font-semibold text-matrix-text md:text-3xl" data-id="element-313">
            Al final obtendrás:
          </motion.h2>

          <motion.ul initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.6,
          delay: 0.15
        }} className="mt-6 space-y-4" data-id="element-314">
            {[{
            icon: Target,
            text: 'Tu Tipo de Jugador (uno de 4 perfiles)'
          }, {
            icon: Eye,
            text: 'Tu "punto ciego" actual (la parte del programa que no estás viendo)'
          }, {
            icon: Zap,
            text: 'Tu primer código recomendado (qué hacer primero para que todo empiece a cambiar)'
          }, {
            icon: Sparkles,
            text: 'Una guía corta para aplicar el cambio en tu vida diaria (sin rituales raros)'
          }].map((item, i) => <li key={i} className="flex items-start gap-3.5" data-id="element-315">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-matrix-gold/20 bg-matrix-gold/5" data-id="element-316">
                  <item.icon className="h-4 w-4 text-matrix-gold" data-id="element-317" />
                </div>
                <span className="font-body text-sm leading-snug text-matrix-text-muted pt-1 md:text-base" data-id="element-318">
                  {item.text}
                </span>
              </li>)}
          </motion.ul>

          {/* Second CTA */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="mt-10 text-center" data-id="element-319">
            <button onClick={onStart} className="group inline-flex items-center gap-2.5 rounded-lg bg-gradient-to-r from-matrix-gold to-matrix-gold-bright px-8 py-4 font-body text-sm font-semibold uppercase tracking-wider text-matrix-bg shadow-[0_0_32px_rgba(201,168,76,0.2)] transition-all duration-300 hover:shadow-[0_0_48px_rgba(201,168,76,0.35)] hover:scale-[1.02] active:scale-[0.98]" data-id="element-320">
              Sí — Quiero mi Mapa del Jugador&#8482;
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" data-id="element-321" />
            </button>
          </motion.div>

          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="mt-6 text-center font-body text-[11px] leading-relaxed text-matrix-text-dim" data-id="element-322">
            Este test es para educación y autoexploración. No reemplaza ayuda
            médica/psicológica profesional.
          </motion.p>
        </div>
      </section>
    </div>;
}